/**
 * Interest Cascading Engine - Manages backwards calculation of interest obligations
 *
 * This module handles the critical backwards calculation logic that was identified as
 * a major source of bugs in the revenue distributor (causing multiple fixes):
 * - Cascades new interest backwards to previous payment periods
 * - Updates future interest obligations correctly (replacement, not accumulation)
 * - Recalculates available revenue after interest obligations
 *
 * Extracted from revenue-distributor.ts lines 312-339 for better isolation and testing
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import {
  calculateAvailableRevenue,
  calculateProrateRatio,
  calculateRevenueImpactRatio,
} from '~/engine/processing/calculators/analysis/capacity-analysis-calculator';
import { calculateAverage } from '~/engine/processing/calculators/metrics/optimization-metrics-calculator';
import type { BondSeries, CashFlow } from '~/types';
import { InterestCalculationEngine } from './interest-calculator';

/**
 * Result of cascade calculation - contains all values to be applied
 * @pure This type represents the output of a pure calculation function
 */
export interface CascadeResult {
  /** Map of bondSeriesId to calculated futureInterestObligation */
  bondObligations: Map<string, number>;
  /** Sum of all bond obligations */
  totalInterestObligation: number;
  /** Revenue minus existing debt minus interest obligation */
  availableForBonds: number;
}

export class InterestCascadingEngine {
  /**
   * Cascades new interest backwards to previous payment periods
   * This is critical for accurate interest calculations and available revenue reduction
   * Uses the correct replacement logic from the prototype (not accumulation)
   *
   * @param recordInProcess - The current record that generated new interest
   * @param recordToRecalculate - The previous record that needs interest obligation updates
   * @param bondSeries - Bond series information for context (optional for backward compatibility)
   */
  static cascadeNewInterestBackwards(
    recordInProcess: CashFlow,
    recordToRecalculate: CashFlow,
    bondSeries?: BondSeries[]
  ): void {
    // Calculate cascade values (pure function)
    const result = InterestCascadingEngine.calculateCascadeValues(
      recordInProcess,
      recordToRecalculate,
      bondSeries
    );

    // Apply results (explicit mutation)
    InterestCascadingEngine.applyCascadeResult(recordToRecalculate, result);
  }

  /**
   * Pure calculation of cascade values without mutation.
   * Calculates what future interest obligations WOULD be for each bond.
   *
   * @param recordInProcess - The current record that generated new interest
   * @param recordToRecalculate - The record to calculate obligations for (read-only)
   * @param bondSeries - Bond series information for first payment proration
   * @returns CascadeResult with calculated obligations and derived values
   * @pure Level 4 engine - pure calculation, no side effects
   */
  static calculateCascadeValues(
    recordInProcess: CashFlow,
    recordToRecalculate: CashFlow,
    bondSeries?: BondSeries[]
  ): CascadeResult {
    const bondObligations = new Map<string, number>();

    // Calculate future interest obligation for each active bond
    for (const bondPayment of recordToRecalculate.bondPayments) {
      if (!bondPayment.isActive) {
        bondObligations.set(bondPayment.bondSeriesId, 0);
        continue;
      }

      let futureObligation = 0;

      // Find matching bond payment in the source record
      const matchingPayment = recordInProcess.bondPayments.find(
        (bp) => bp.bondSeriesId === bondPayment.bondSeriesId && bp.isActive
      );

      if (matchingPayment) {
        futureObligation = matchingPayment.totalInterest;

        // Check if this is the first payment for this bond series - apply proration
        if (bondSeries) {
          const series = bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
          if (series && recordToRecalculate.paymentDate === series.firstInterestPaymentDate) {
            // This is the first payment - prorate the future interest obligation
            const actualDays = InterestCalculationEngine.days360(
              series.issueDate,
              series.firstInterestPaymentDate
            );
            const standardDays = TIME_PERIODS.STANDARD_PERIOD_DAYS;
            const prorateRatio = calculateProrateRatio(actualDays, standardDays);

            futureObligation = Math.round(futureObligation * prorateRatio);
          }
        }
      }

      bondObligations.set(bondPayment.bondSeriesId, futureObligation);
    }

    // Calculate derived values
    const totalInterestObligation = Array.from(bondObligations.values()).reduce(
      (sum, val) => sum + val,
      0
    );

    return {
      bondObligations,
      totalInterestObligation,
      availableForBonds: recordToRecalculate.revenueMinusExistingDebt - totalInterestObligation,
    };
  }

  /**
   * Applies cascade calculation results to a cash flow record.
   * This is the explicit mutation point - separates calculation from side effects.
   *
   * @param record - The cash flow record to update
   * @param result - The cascade result from calculateCascadeValues()
   */
  static applyCascadeResult(record: CashFlow, result: CascadeResult): void {
    // Apply bond-level obligations
    for (const bondPayment of record.bondPayments) {
      const obligation = result.bondObligations.get(bondPayment.bondSeriesId);
      if (obligation !== undefined) {
        bondPayment.futureInterestObligation = obligation;
      }
    }

    // Apply record-level values
    record.interestObligation = result.totalInterestObligation;
    record.availableForBonds = result.availableForBonds;
  }

  /**
   * Updates future interest obligations for bond payments in the target record
   * Maps interest obligations by bond series ID to ensure accurate allocation
   *
   * @param recordInProcess - The source record with new interest calculations
   * @param recordToRecalculate - The target record to update with future obligations
   * @param bondSeries - Bond series information for first payment proration
   */
  static updateFutureInterestObligations(
    recordInProcess: CashFlow,
    recordToRecalculate: CashFlow,
    bondSeries?: BondSeries[]
  ): void {
    // Update bond payments with new future interest obligations
    for (const bondPayment of recordToRecalculate.bondPayments) {
      // Only update future interest obligations on active bond payments
      if (bondPayment.isActive) {
        let futureObligation = 0;

        // Find matching bond payment in the source record
        const matchingPayment = recordInProcess.bondPayments.find(
          (bp) => bp.bondSeriesId === bondPayment.bondSeriesId && bp.isActive
        );

        if (matchingPayment) {
          futureObligation = matchingPayment.totalInterest;

          // Check if this is the first payment for this bond series - apply proration
          if (bondSeries) {
            const series = bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
            if (series && recordToRecalculate.paymentDate === series.firstInterestPaymentDate) {
              // This is the first payment - prorate the future interest obligation
              const actualDays = InterestCalculationEngine.days360(
                series.issueDate,
                series.firstInterestPaymentDate
              );
              const standardDays = TIME_PERIODS.STANDARD_PERIOD_DAYS;
              const prorateRatio = calculateProrateRatio(actualDays, standardDays);

              futureObligation = Math.round(futureObligation * prorateRatio);
            }
          }
        }

        bondPayment.futureInterestObligation = futureObligation;
      } else {
        // Ensure inactive bond payments have no future interest obligation
        bondPayment.futureInterestObligation = 0;
      }
    }
  }

  /**
   * Validates that interest cascading has been applied correctly
   * Useful for testing and debugging cascading logic
   *
   * @param cashFlows - Complete cash flow sequence to validate
   * @returns Validation result with any issues found
   */
  static validateInterestCascading(cashFlows: CashFlow[]): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];

    for (let i = 0; i < cashFlows.length; i++) {
      // Check that interest obligations are properly set on previous records
      for (let j = 0; j < i; j++) {
        const previousRecord = cashFlows[j];

        // Verify that previous records have been updated with interest obligations
        if (
          previousRecord.interestObligation === undefined ||
          previousRecord.interestObligation < 0
        ) {
          issues.push(
            `Invalid interest obligation at record ${j}: ${previousRecord.interestObligation}`
          );
        }

        // Verify that availableForBonds reflects interest obligations
        const expectedAvailable = calculateAvailableRevenue(
          previousRecord.revenueMinusExistingDebt,
          0,
          previousRecord.interestObligation
        );
        if (Math.abs(previousRecord.availableForBonds - expectedAvailable) > 0.01) {
          issues.push(
            `Available for bonds calculation error at record ${j}: expected ${expectedAvailable}, got ${previousRecord.availableForBonds}`
          );
        }
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Gets cascading statistics for analysis and debugging
   * Provides insights into how interest obligations flow backwards through the cash flow sequence
   *
   * @param cashFlows - Complete cash flow sequence to analyze
   * @returns Statistics about interest cascading effects
   */
  static getCascadingStatistics(cashFlows: CashFlow[]): {
    totalInterestObligations: number;
    maxInterestObligation: number;
    recordsWithObligations: number;
    averageObligation: number;
    cascadeImpactByPeriod: { period: number; obligation: number; revenueImpact: number }[];
  } {
    const obligations = cashFlows.map((cf) => cf.interestObligation || 0);
    const totalObligations = obligations.reduce((sum, o) => sum + o, 0);
    const recordsWithObligations = obligations.filter((o) => o > 0).length;

    const cascadeImpactByPeriod = cashFlows.map((cf, index) => ({
      period: index,
      obligation: cf.interestObligation || 0,
      revenueImpact: calculateRevenueImpactRatio(
        cf.interestObligation || 0,
        cf.revenueMinusExistingDebt || 1
      ),
    }));

    return {
      totalInterestObligations: totalObligations,
      maxInterestObligation: Math.max(...obligations),
      recordsWithObligations,
      averageObligation: calculateAverage(totalObligations, recordsWithObligations),
      cascadeImpactByPeriod,
    };
  }
}
